import React from 'react';
import { clsx } from 'clsx';
import { ArrowLeftIcon, XMarkIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Button } from '../Button/Button';
import { Loading } from './components/loading';
import { MoreOptionsMenu, type MoreOption } from './MoreOptionsMenu';
import { StateBadge } from './StateBadge';
import { accionDeEstadoAlCanon, clasesDeTonoBoton, TONO_ANULAR, type AccionTono } from './stateActionCanon';
import { useMasterDocumentTranslation } from './i18n';
import type { ActionBarProps } from './MasterDocument.types';

// ─── ActionBar ─────────────────────────────────────────────

/**
 * Cabecera del documento, al canon del detalle de MasterCrud (ADR-027 §H.1).
 *
 * **Cambio de forma deliberado.** Antes era una barra horizontal de 52px con
 * todo en una línea —«Devolución» como breadcrumb, el número, los badges y
 * tantos botones como cupieran, repartidos midiendo anchos— y no se parecía a
 * la cabecera de un maestro. Ahora reproduce la de MasterCrud, que apila:
 *
 * ```
 * ← Volver
 * Devolución de Venta · DEV-0001            [Cancelar] [Guardar] [⋮]
 * [🏢 Compañía] [Aprobado] [Nota crédito]
 * ```
 *
 * Lo que fija el canon y aquí se copia literal (Regla A.2 — copiar, nunca
 * aproximar): el enlace «Volver» en `text-sm` `content-tertiary` con hover a
 * `#1355b7`; el título en `text-xl font-semibold tracking-tight text-[#1355b7]`;
 * y las acciones en `h-9`.
 *
 * **Sólo `Cancelar` y `Guardar` quedan visibles** (§H.1: «dos botones, dos
 * palabras»); el resto —transiciones incluidas— vive en «Más acciones». El
 * reparto ya no depende de cuánto mida cada rótulo: antes lo decidía un cálculo
 * de anchos, así que la misma pantalla ofrecía distintos botones según el ancho
 * de la ventana o el idioma. Ahora es una regla, no una medición.
 *
 * Se conserva TODA la maquinaria de comportamiento previa: `disabled` con su
 * `disabledReason` como `title`, el indicador de acción en vuelo por `leftIcon`
 * (el `Button` del kit no tiene prop `loading`) y el agrupado
 * transiciones/acciones del desplegable.
 *
 * @see MasterDocument.types - ActionBarProps
 */
export function ActionBar({
  breadcrumbLabel,
  documentTitle,
  companyName,
  onBreadcrumbClick,
  documentId,
  documentNumber,
  stateBadge,
  docTypeBadge,
  transitions,
  onTransition,
  actions,
  className,
}: ActionBarProps) {
  const { t } = useMasterDocumentTranslation();

  type UnifiedAction = {
    key: string;
    label: string;
    icon?: React.ReactNode;
    variant: 'primary' | 'secondary' | 'danger' | 'ghost';
    onClick: () => void;
    disabled?: boolean;
    disabledReason?: string;
    loading?: boolean;
    group: 'transition' | 'action';
  };

  /**
   * Acciones que se ofrecen como BOTÓN, por clave y no por rótulo —el rótulo es dato
   * de catálogo y cambia con el idioma—. El resto (auditoría, transiciones) vive en
   * «Más acciones».
   *
   * `save` es la primaria del canon; `cancel` e `print` se promueven porque son las
   * dos operaciones que el usuario busca sobre un documento ya emitido y tenerlas a
   * dos clics dentro de un menú cerrado las escondía.
   */
  const CLAVES_DIRECTAS = ['save', 'cancel', 'print'];
  const directas = actions.filter((a) => CLAVES_DIRECTAS.includes(a.key) && a.position !== 'moreMenu');
  const guardar = directas.find((a) => a.key === 'save');
  const anular = directas.find((a) => a.key === 'cancel');
  const imprimir = directas.find((a) => a.key === 'print');

  const overflowActions: UnifiedAction[] = [
    ...transitions.map((tr) => ({
      key: `t-${tr.actionKey ?? tr.to}`,
      label: tr.label,
      icon: tr.icon,
      variant: tr.style,
      onClick: () => onTransition(tr.to),
      disabled: tr.disabled,
      loading: tr.loading,
      group: 'transition' as const,
    })),
    ...actions
      .filter((a) => !directas.includes(a) && a.position !== 'moreMenu')
      .map((a) => ({
        key: `a-${a.key}`,
        label: a.label,
        icon: a.icon,
        variant: a.variant ?? ('ghost' as const),
        onClick: a.onClick,
        disabled: a.disabled,
        disabledReason: a.disabledReason,
        loading: a.loading,
        group: 'action' as const,
      })),
  ];

  const moreMenuActions = actions.filter((a) => a.position === 'moreMenu');

  // El `Button` del kit no tiene prop `loading` (ver Button.types.ts), así que
  // el indicador entra por `leftIcon`, sustituyendo al icono de la acción. En el
  // desplegable ocupa el mismo hueco `icon`.
  const iconOf = (item: { icon?: React.ReactNode; loading?: boolean }): React.ReactNode =>
    item.loading
      // `inheritColor`: el fondo del botón varía con la variante — un primario
      // pinta `bg-primary-600`, el mismo color del spinner por defecto, y ahí
      // sería invisible. Tomando `currentColor` se ve como el icono al que
      // sustituye, sea cual sea la variante.
      ? (
        <Loading
          size="sm"
          layout="inline"
          inheritColor
          ariaLabel={t('master_document.chrome.actionRunning')}
        />
      )
      : item.icon;

  const dropdownItems: MoreOption[] = [];

  const overflowTransitions = overflowActions.filter((i) => i.group === 'transition');
  const overflowPlain = overflowActions.filter((i) => i.group === 'action');

  if (overflowTransitions.length > 0) {
    dropdownItems.push({ key: 'h-transitions', isHeading: true, label: t('master_document.actionBar.transitions') });
    overflowTransitions.forEach((item) => {
      dropdownItems.push({
        key: item.key,
        label: item.label,
        icon: iconOf(item),
        onClick: item.onClick,
        disabled: item.disabled,
        danger: item.variant === 'danger',
      });
    });
  }

  if (overflowPlain.length > 0) {
    if (overflowTransitions.length > 0) dropdownItems.push({ key: 'd-1', isDivider: true });
    dropdownItems.push({ key: 'h-actions', isHeading: true, label: t('master_document.actionBar.actions') });
    overflowPlain.forEach((item) => {
      dropdownItems.push({
        key: item.key,
        label: item.label,
        icon: iconOf(item),
        onClick: item.onClick,
        disabled: item.disabled,
        description: item.disabled ? item.disabledReason : undefined,
        danger: item.variant === 'danger',
      });
    });
  }

  if (moreMenuActions.length > 0) {
    if (dropdownItems.length > 0) dropdownItems.push({ key: 'd-2', isDivider: true });
    moreMenuActions.forEach((a) => {
      dropdownItems.push({
        key: `m-${a.key}`,
        label: a.label,
        icon: iconOf(a),
        onClick: a.onClick,
        disabled: a.disabled,
        description: a.disabled ? a.disabledReason : undefined,
        danger: a.variant === 'danger',
      });
    });
  }

  const showOverflowBtn = dropdownItems.length > 0;
  // Una acción del desplegable en vuelo no se vería: el menú está cerrado. El
  // indicador sube al disparador, que es el único punto visible de esa acción —
  // el mismo criterio que la celda de acciones del listado.
  const algoEnVuelo = [...overflowActions, ...moreMenuActions].some((a) => a.loading);
  const numero = documentNumber || documentId;

  return (
    <div
      className={clsx('w-full px-4 md:px-6 pt-4 md:pt-5 pb-4 space-y-3', className)}
      role="toolbar"
      aria-label={t('master_document.actionBar.aria')}
    >
      {/* «Volver»: el canon nombra el destino, no la dirección. Antes decía el
          nombre del documento («Devolución»), que se leía como una migaja de pan
          y no como la salida de la pantalla. */}
      <nav>
        <button
          type="button"
          onClick={onBreadcrumbClick}
          className={clsx(
            'inline-flex items-center gap-1 text-sm text-content-tertiary dark:text-dark-content-tertiary',
            'hover:text-[#1355b7] transition-colors',
            'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 rounded-md',
            'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
          )}
        >
          <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" />
          <span>{t('master_document.chrome.backToList')}</span>
        </button>
      </nav>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          {/* Título + número: la identidad del documento. El título es el rótulo
              de la clase (`identity.label`), que es el análogo del nombre del
              maestro en MasterCrud; el número lo acompaña porque es lo que el
              usuario busca al llegar. */}
          <div className="flex items-baseline gap-2 min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600 truncate">
              {documentTitle || breadcrumbLabel}
            </h1>
            {numero && (
              <>
                <span className="text-content-tertiary dark:text-dark-content-tertiary shrink-0" aria-hidden="true">·</span>
                <span className="text-xl font-semibold tracking-tight text-content-primary dark:text-dark-content-primary tabular-nums truncate">
                  {numero}
                </span>
              </>
            )}
          </div>

          {/* Compañía + estado + tipo, todos como BADGE. La compañía reproduce el
              `CompanySelector` de MasterCrud en su modo de sólo lectura —un
              `<span>`, no un control—, porque aquí no se puede cambiar. */}
          <div className="flex items-center gap-2 flex-wrap">
            {companyName && (
              <span className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold leading-none select-none',
                'bg-[#eff8ff] dark:bg-primary-900/20 border border-[#93d1fd] dark:border-primary-800',
                'text-[#1355b7] dark:text-primary-300',
              )}>
                <BuildingOffice2Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                <span className="max-w-[180px] truncate" title={companyName}>{companyName}</span>
              </span>
            )}
            {stateBadge && <StateBadge {...stateBadge} />}
            {docTypeBadge && (
              // Badge, no píldora: misma geometría que `StateBadge` (radio 4px,
              // 12px/500, padding 2/8). Antes era `rounded-full` 10.5px/600, que
              // junto al badge de estado se leía como dos sistemas distintos.
              <span className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded text-xs leading-4 font-medium select-none',
                'bg-background-secondary text-content-secondary',
                'dark:bg-dark-bg-secondary dark:text-dark-content-secondary',
              )}>
                {docTypeBadge}
              </span>
            )}
          </div>
        </div>

        {/* Acciones: Cancelar · Guardar · kebab (§H.1) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {guardar && (
            <Button
              type="outline"
              size="base"
              className="!h-9 !px-3 !text-sm font-medium"
              leftIcon={<XMarkIcon className="w-4 h-4" />}
              onClick={onBreadcrumbClick}
            >
              {t('master_document.chrome.cancel')}
            </Button>
          )}

          {/* «Anular» va SEPARADA de «Guardar» por «Imprimir» a propósito: es la única
              acción destructiva de la barra y dejarla pegada a la primaria invita al
              clic equivocado. Su rótulo, su icono y su tono salen del canon de la
              acción de estado, los mismos que en la fila del listado. */}
          {anular && (() => {
            const canon = accionDeEstadoAlCanon({ key: anular.key, label: anular.label, variant: anular.variant }, t);
            const tone: AccionTono = canon?.tone ?? TONO_ANULAR;
            const rotulo = canon?.label ?? anular.label;
            return (
              <span title={anular.disabled && anular.disabledReason ? anular.disabledReason : undefined} className="inline-flex">
                <Button
                  type="outline"
                  size="base"
                  className={clsx('!h-9 !px-3 !text-sm font-medium', clasesDeTonoBoton(tone))}
                  leftIcon={anular.loading ? iconOf(anular) : (canon ? <canon.Icon className="w-4 h-4" /> : anular.icon)}
                  onClick={anular.onClick}
                  disabled={anular.disabled}
                  title={!anular.disabled ? rotulo : undefined}
                  ariaLabel={rotulo}
                >
                  {rotulo}
                </Button>
              </span>
            );
          })()}

          {imprimir && (
            <span title={imprimir.disabled && imprimir.disabledReason ? imprimir.disabledReason : undefined} className="inline-flex">
              <Button
                type="outline"
                size="base"
                className="!h-9 !px-3 !text-sm font-medium"
                leftIcon={iconOf(imprimir)}
                onClick={imprimir.onClick}
                disabled={imprimir.disabled}
                title={!imprimir.disabled ? imprimir.label : undefined}
                ariaLabel={imprimir.label}
              >
                {imprimir.label}
              </Button>
            </span>
          )}

          {guardar && (
            <span
              title={guardar.disabled && guardar.disabledReason ? guardar.disabledReason : undefined}
              className="inline-flex"
            >
              <Button
                type="default"
                size="base"
                className="!h-9 !px-3 !text-sm font-medium"
                leftIcon={iconOf(guardar)}
                onClick={guardar.onClick}
                disabled={guardar.disabled}
                title={!guardar.disabled ? guardar.label : undefined}
                ariaLabel={guardar.label}
              >
                {guardar.label}
              </Button>
            </span>
          )}

          {showOverflowBtn && (
            <MoreOptionsMenu
              options={dropdownItems}
              busy={algoEnVuelo}
              ariaLabel={t('master_document.actionBar.moreActions')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
